// Copyright (c) Bentley Systems
import { useCallback, useEffect, useRef, useState } from "react";
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import { mapDataToModels } from "../Helpers/map-response";
import { Organization } from "./Models/organizations";
import { OrgSubscription } from "./Models/org-subscription";
import { useODataTableState } from "../Helpers/useODataTableState";

/**
 * Hook encapsulating data fetching and mutations for the SubscribedOrganizations page.
 *
 * Manages two OData-backed tables:
 * 1. Organizations list — paginated, sortable, filterable
 * 2. Subscriptions list — shown when an organization is selected
 *
 * Also provides `unsubscribeProject` (DELETE) and a `refresh` helper.
 * Auto-fetches data when dependencies (pagination, sort, filters) change.
 */
type UseOrganizationSubscriptionsProps = {
  accessToken: string;
  dispatch: AppDispatch;
  isNodeAllowed: boolean;
};

export const useOrganizationSubscriptions = ({
  accessToken,
  dispatch,
  isNodeAllowed,
}: UseOrganizationSubscriptionsProps) => {
  // --- Organization state ---
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<
    Organization | undefined
  >(undefined);

  // --- Subscription state ---
  const [organizationSubscriptions, setOrganizationSubscriptions] = useState<
    OrgSubscription[]
  >([]);

  // --- OData table state for organizations ---
  const orgColumnToODataPath: Record<string, string> = { Name: "Name" };

  const {
    pageSize: orgPageSize,
    isLoading: isOrgLoading,
    sortBy: orgSortBy,
    tableFiltersState: orgTableFiltersState,
    setIsLoading: setOrgIsLoading,
    setTotalCount: setOrgTotalCount,
    handleSort: handleOrgSort,
    handleFilter: handleOrgFilter,
    paginator: orgPaginator,
    buildOrderBy: buildOrgOrderBy,
    buildFilterStr: buildOrgFilterStr,
    skip: orgSkip,
    top: orgTop,
  } = useODataTableState({
    defaultSortBy: [{ id: "Name", desc: false }],
  });

  // --- OData table state for subscriptions ---
  const projColumnToODataPath: Record<string, string> = {
    projectName: "Project/Name",
    uniqueName: "Project/UniqueName",
    projectVersion: "Project/Version",
    subscribedOn: "SubscribedOn",
    subscribedByName: "SubscribedByName",
  };

  const {
    sortBy: projSortBy,
    isLoading: isProjLoading,
    tableFiltersState: projTableFiltersState,
    setIsLoading: setProjIsLoading,
    setTotalCount: setProjTotalCount,
    handleSort: handleProjSort,
    handleFilter: handleProjFilter,
    paginator: projPaginator,
    buildOrderBy: buildProjOrderBy,
    buildFilterStr: buildProjFilterStr,
    skip: projSkip,
    top: projTop,
    pageSize: projPageSize,
  } = useODataTableState({
    defaultSortBy: [{ id: "projectName", desc: false }],
  });

  // --- Fetch organizations ---
  const fetchOrganizations = useCallback(
    async (signal?: AbortSignal) => {
      setOrgIsLoading(true);
      const url =
        `${ConfigManager.projectProviderUrl}/odata/Organizations?$count=true&$skip=${orgSkip}&$top=${orgTop}` +
        `${buildOrgOrderBy(orgColumnToODataPath) || "&$orderby=Name asc"}${buildOrgFilterStr(orgColumnToODataPath)}`;
      const runId = crypto.randomUUID();
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        const data = await response.json();
        const organizationDtos: Organization[] =
          mapDataToModels<Organization>(data.value);
        setOrganizations(organizationDtos);
        setOrgTotalCount(data["@odata.count"] ?? 0);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setOrgIsLoading(false);
      }
    },
    [
      accessToken,
      dispatch,
      orgSkip,
      orgTop,
      buildOrgOrderBy,
      buildOrgFilterStr,
      setOrgIsLoading,
      setOrgTotalCount,
    ],
  );

  // --- Auto-fetch organizations ---
  useEffect(() => {
    if (isNodeAllowed && selectedOrganization === undefined) {
      const abortController = new AbortController();
      void fetchOrganizations(abortController.signal);
      return () => {
        abortController.abort();
      };
    }
  }, [fetchOrganizations, isNodeAllowed, selectedOrganization]);

  // --- Fetch subscriptions ---
  const fetchSubscriptions = useCallback(
    async (orgId?: string, signal?: AbortSignal) => {
      const orgToUse = orgId || selectedOrganization?.Id;
      if (!orgToUse) {
        return;
      }
      setProjIsLoading(true);
      const runId = crypto.randomUUID();
      const url =
        `${ConfigManager.projectProviderUrl}/odata/Organizations(${orgToUse})/Subscriptions?$count=true&$expand=Project($select=Name,UniqueName,Version)&$skip=${projSkip}&$top=${projTop}${ 
        buildProjOrderBy(projColumnToODataPath) || "&$orderby=Project/Name asc" 
        }${buildProjFilterStr(projColumnToODataPath)}`;
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        const data = await response.json();
        const newSubs = mapDataToModels<OrgSubscription>(data.value);
        newSubs.forEach((s) => {
          s.SubscribedOn = new Date(s.SubscribedOn);
          s.Project.LastChange = new Date(s.Project.LastChange);
        });
        setOrganizationSubscriptions(newSubs);
        setProjTotalCount(data["@odata.count"] ?? 0);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setProjIsLoading(false);
      }
    },
    [
      selectedOrganization,
      accessToken,
      dispatch,
      projSkip,
      projTop,
      buildProjOrderBy,
      buildProjFilterStr,
      setProjIsLoading,
      setProjTotalCount,
    ],
  );

  // --- Auto-fetch subscriptions ---
  useEffect(() => {
    if (selectedOrganization !== undefined) {
      const abortController = new AbortController();
      void fetchSubscriptions(undefined, abortController.signal);
      return () => abortController.abort();
    }
  }, [fetchSubscriptions, selectedOrganization]);

  // --- Unsubscribe project ---
  const unsubscribeProject = useCallback(
    async (orgSub: OrgSubscription): Promise<boolean> => {
      let result = false;
      const deleteUrl =
        `${ConfigManager.projectProviderUrl}/api/Projects/${orgSub.Project.UniqueName}/organizations/${orgSub.OrganizationId}`;
      const runIdDel = crypto.randomUUID();
      try {
        const response = await fetch(deleteUrl, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runIdDel,
          },
        });
        if (!response.ok) {
          setErrorState(response.status, dispatch, runIdDel, true);
        } else {
          const data = await response.json();
          if (!data.isSuccess) {
            setErrorState(response.status, dispatch, runIdDel, true);
          } else {
            result = true;
          }
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runIdDel, true);
        }
      }

      return result;
    },
    [accessToken, dispatch],
  );

  // --- Refresh ---
  const refreshAbortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    refreshAbortRef.current?.abort();
    refreshAbortRef.current = new AbortController();
    if (selectedOrganization) {
      void fetchSubscriptions(undefined, refreshAbortRef.current.signal);
    } else {
      void fetchOrganizations(refreshAbortRef.current.signal);
    }
  }, [selectedOrganization, fetchSubscriptions, fetchOrganizations]);

  return {
    // Organization data
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    isOrgLoading,
    orgPageSize,
    orgSortBy,
    orgTableFiltersState,
    handleOrgSort,
    handleOrgFilter,
    orgPaginator,
    fetchOrganizations,

    // Subscription data
    organizationSubscriptions,
    setOrganizationSubscriptions,
    isProjLoading,
    projPageSize,
    projSortBy,
    projTableFiltersState,
    handleProjSort,
    handleProjFilter,
    projPaginator,
    fetchSubscriptions,

    // Mutations
    unsubscribeProject,
    refresh,
  };
};
